import React, { useEffect, useState } from 'react';
import { FiAlertCircle, FiCheck, FiClock, FiDollarSign, FiInfo, FiSliders, FiTarget, FiTrendingUp } from 'react-icons/fi';
import { saveRevenueSettings, updateOpportunity } from '../../../services/CardService';
import {
  DEFAULT_REVENUE_SETTINGS,
  type RevenueItem,
  type RevenueRadar as RevenueRadarData,
  type RevenueSettings,
} from '../../../services/revenueTypes';
import { daysLabel, formatBrl, formatDelta, formatMoney, formatWeight, parseMoneyInput } from '../../../utils/revenueFormat';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import TemperatureChip from './TemperatureChip';
import './revenue.css';

export type RadarList = 'paradas' | 'sem-valor' | 'fechados';

interface Props {
  sectorId: number;
  radar: RevenueRadarData | null;
  loading?: boolean;
  error?: string | null;
  /** Lista aberta ao chegar (link da Central: ?radar=sem-valor). */
  initialList?: RadarList | null;
  onReload: () => void;
  onOpen: (path: string) => void;
}

const LIST_TITLES: Record<RadarList, string> = {
  paradas: 'Oportunidades paradas',
  'sem-valor': 'Oportunidades sem valor',
  fechados: 'Fechadas no mês',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Radar de Receita da conexão: quanto está em negociação (R$), quanto vale ponderado pela temperatura, o que está parado,
 * o que não tem valor (com atalho para registrar) e o que fechou no mês. Tudo vem dos cards (fonte única de valor).
 */
const RevenueRadar: React.FC<Props> = ({ sectorId, radar, loading, error, initialList, onReload, onOpen }) => {
  const [list, setList] = useState<RadarList | null>(initialList ?? null);
  const [showHow, setShowHow] = useState(false);
  const [editingWeights, setEditingWeights] = useState(false);
  const [weights, setWeights] = useState<Record<keyof RevenueSettings, string>>({
    weightHot: '', weightWarm: '', weightCold: '', stalledDays: '',
  });
  const [values, setValues] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | 'weights' | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (initialList) setList(initialList);
  }, [initialList]);

  const settings: RevenueSettings = (radar && radar.settings[String(sectorId)]) || DEFAULT_REVENUE_SETTINGS;

  const openWeights = () => {
    setWeights({
      weightHot: formatWeight(settings.weightHot),
      weightWarm: formatWeight(settings.weightWarm),
      weightCold: formatWeight(settings.weightCold),
      stalledDays: String(settings.stalledDays),
    });
    setEditingWeights(true);
    setMessage(null);
  };

  const parseWeight = (raw: string) => Number(raw.trim().replace(',', '.'));

  const saveWeights = async () => {
    if ([weights.weightHot, weights.weightWarm, weights.weightCold, weights.stalledDays].some((v) => !v.trim())) {
      setMessage({ tone: 'error', text: 'Preencha todos os campos: Quente, Morna, Fria e Parada após (dias).' });
      return;
    }
    const next: RevenueSettings = {
      weightHot: parseWeight(weights.weightHot),
      weightWarm: parseWeight(weights.weightWarm),
      weightCold: parseWeight(weights.weightCold),
      stalledDays: Math.round(Number(weights.stalledDays.trim())),
    };
    if ([next.weightHot, next.weightWarm, next.weightCold].some((w) => !Number.isFinite(w) || w < 0 || w > 1)) {
      setMessage({ tone: 'error', text: 'Cada peso precisa ficar entre 0 e 1 (ex.: 0,6).' });
      return;
    }
    if (!Number.isFinite(next.stalledDays) || next.stalledDays < 1 || next.stalledDays > 365) {
      setMessage({ tone: 'error', text: 'Dias para considerar parada: entre 1 e 365.' });
      return;
    }
    setBusy('weights');
    try {
      await saveRevenueSettings(sectorId, next);
      setEditingWeights(false);
      setMessage({ tone: 'ok', text: 'Pesos salvos para esta conexão.' });
      onReload();
    } catch (e) {
      setMessage({ tone: 'error', text: apiErrorMessage(e, 'Não foi possível salvar os pesos.') });
    } finally {
      setBusy(null);
    }
  };

  const saveValue = async (item: RevenueItem) => {
    const raw = values[item.cardId] ?? '';
    const value = parseMoneyInput(raw);
    if (!raw.trim() || !Number.isFinite(value) || value <= 0) {
      setMessage({ tone: 'error', text: `Valor inválido para ${item.contactName}.` });
      return;
    }
    setBusy(item.cardId);
    try {
      await updateOpportunity(item.cardId, { estimatedValue: value, currency: 'BRL' });
      setValues((v) => {
        const next = { ...v };
        delete next[item.cardId];
        return next;
      });
      setMessage({ tone: 'ok', text: `Valor de ${item.contactName} registrado (fica no histórico de correções).` });
      onReload();
    } catch (e) {
      setMessage({ tone: 'error', text: apiErrorMessage(e, 'Não foi possível registrar o valor.') });
    } finally {
      setBusy(null);
    }
  };

  if (loading && !radar) {
    return (
      <section className="rev-radar is-loading" aria-busy="true" aria-label="Radar de Receita">
        <span className="mai-skeleton-bar" />
        <span className="mai-skeleton-bar is-short" />
      </section>
    );
  }
  if (error && !radar) {
    return (
      <section className="rev-radar" aria-label="Radar de Receita">
        <p className="rev-radar__error" role="alert"><FiAlertCircle aria-hidden /> {error}</p>
      </section>
    );
  }
  if (!radar) return null;

  const month = radar.closedMonth;
  const others = radar.otherCurrencies.reduce((n, c) => n + c.count, 0);
  const brlValued = Math.max(0, radar.valuedCount - others);
  const delta = formatDelta(month.deltaPct);
  const items: RevenueItem[] = list === 'paradas' ? radar.stalled : list === 'sem-valor' ? radar.withoutValue : list === 'fechados' ? month.cases : [];
  const total = list === 'paradas' ? radar.stalledCount : list === 'sem-valor' ? radar.withoutValueCount : list === 'fechados' ? month.count : 0;
  const toggle = (key: RadarList) => setList((cur) => (cur === key ? null : key));

  return (
    <section className="rev-radar" aria-label="Radar de Receita" data-testid="revenue-radar">
      <header className="rev-radar__head">
        <div>
          <h3><FiTarget aria-hidden /> Radar de Receita</h3>
          <p>Valores dos cards desta conexão. Nada aqui é estimado: card sem valor não conta como zero.</p>
        </div>
        <div className="rev-radar__head-actions">
          <button type="button" className="mai-btn mai-btn-ghost" onClick={() => setShowHow((v) => !v)} aria-expanded={showHow}>
            <FiInfo size={14} aria-hidden /> Como é calculado
          </button>
          <button type="button" className="mai-btn mai-btn-ghost" onClick={openWeights}>
            <FiSliders size={14} aria-hidden /> Ajustar pesos
          </button>
        </div>
      </header>

      <div className="rev-radar__tiles">
        <div className="rev-tile">
          <span className="rev-tile__label"><FiDollarSign aria-hidden /> Em negociação</span>
          <strong data-testid="radar-pipeline">{formatBrl(radar.pipelineValue)}</strong>
          <span className="rev-tile__sub">
            {radar.openCount === 0
              ? 'Nenhuma oportunidade em andamento'
              : `${brlValued} de ${plural(radar.openCount, 'oportunidade', 'oportunidades')} com valor`}
            {others > 0 && ` · ${plural(others, 'em outra moeda ou sem moeda', 'em outra moeda ou sem moeda')} fora`}
          </span>
        </div>
        <div className="rev-tile">
          <span className="rev-tile__label"><FiTrendingUp aria-hidden /> Ponderado pela temperatura</span>
          <strong data-testid="radar-weighted">{formatBrl(radar.weighted)}</strong>
          <span className="rev-tile__sub">
            quente {formatWeight(settings.weightHot)} · morna {formatWeight(settings.weightWarm)} · fria {formatWeight(settings.weightCold)}
            {radar.unweightedCount > 0 && ` · ${plural(radar.unweightedCount, 'sem temperatura', 'sem temperatura')} fora`}
          </span>
        </div>
        <button
          type="button"
          className={`rev-tile rev-tile--action${list === 'paradas' ? ' is-active' : ''}${radar.stalledCount > 0 ? ' is-alert' : ''}`}
          onClick={() => toggle('paradas')}
          aria-pressed={list === 'paradas'}
        >
          <span className="rev-tile__label"><FiClock aria-hidden /> Paradas há mais de {daysLabel(radar.stalledDays)}</span>
          <strong data-testid="radar-stalled">{radar.stalledCount}</strong>
          <span className="rev-tile__sub">{radar.stalledValue > 0 ? `${formatBrl(radar.stalledValue)} parados` : 'Ver a lista'}</span>
        </button>
        <button
          type="button"
          className={`rev-tile rev-tile--action${list === 'sem-valor' ? ' is-active' : ''}`}
          onClick={() => toggle('sem-valor')}
          aria-pressed={list === 'sem-valor'}
        >
          <span className="rev-tile__label"><FiAlertCircle aria-hidden /> Sem valor</span>
          <strong data-testid="radar-without-value">{radar.withoutValueCount}</strong>
          <span className="rev-tile__sub">{radar.withoutValueCount > 0 ? 'Registrar valores' : 'Todas têm valor'}</span>
        </button>
        <button
          type="button"
          className={`rev-tile rev-tile--action${list === 'fechados' ? ' is-active' : ''}`}
          onClick={() => toggle('fechados')}
          aria-pressed={list === 'fechados'}
          disabled={!radar.hasWonColumn}
          title={radar.hasWonColumn ? undefined : 'O funil não tem coluna de Ganho: marque o tipo "ganho" na coluna de venda fechada do CRM.'}
        >
          <span className="rev-tile__label"><FiCheck aria-hidden /> Fechado em {month.label || 'este mês'}</span>
          <strong data-testid="radar-closed-month">{radar.hasWonColumn ? formatBrl(month.value) : 'Indisponível'}</strong>
          <span className="rev-tile__sub">
            {radar.hasWonColumn
              ? `${plural(month.count, 'venda', 'vendas')} · ${month.previousLabel} até o mesmo dia: ${formatBrl(month.previousValue)}${delta ? ` (${delta})` : ''}`
              : 'Sem coluna de Ganho no funil'}
          </span>
        </button>
      </div>

      <div className="rev-radar__temps" aria-label="Oportunidades por temperatura">
        {radar.temperatures.map((t) => (
          <span key={t.temperature} className="rev-radar__temp">
            <TemperatureChip temperature={t.temperature} compact />
            <span>{plural(t.count, 'oportunidade', 'oportunidades')}</span>
            <span className="rev-radar__muted">{formatBrl(t.value)} × {formatWeight(t.weight)} = {formatBrl(t.weighted)}</span>
          </span>
        ))}
        {radar.unweightedCount > 0 && (
          <span className="rev-radar__temp rev-radar__muted">
            Sem temperatura: {plural(radar.unweightedCount, 'oportunidade', 'oportunidades')} ({formatBrl(radar.unweightedValue)}), fora do ponderado
          </span>
        )}
      </div>

      {message && (
        <p className={`rev-radar__msg is-${message.tone}`} role={message.tone === 'error' ? 'alert' : 'status'}>{message.text}</p>
      )}

      {showHow && (
        <ul className="rev-radar__how" data-testid="radar-how">
          {radar.howCalculated.map((h) => <li key={h}>{h}</li>)}
        </ul>
      )}

      {editingWeights && (
        <div className="rev-radar__weights" data-testid="radar-weights">
          <p>O valor ponderado multiplica o valor de cada oportunidade pelo peso da temperatura dela. Vale só para esta conexão.</p>
          <div className="rev-radar__weights-row">
            <label>Quente<input inputMode="decimal" value={weights.weightHot} onChange={(e) => setWeights({ ...weights, weightHot: e.target.value })} /></label>
            <label>Morna<input inputMode="decimal" value={weights.weightWarm} onChange={(e) => setWeights({ ...weights, weightWarm: e.target.value })} /></label>
            <label>Fria<input inputMode="decimal" value={weights.weightCold} onChange={(e) => setWeights({ ...weights, weightCold: e.target.value })} /></label>
            <label>Parada após (dias)<input inputMode="numeric" value={weights.stalledDays} onChange={(e) => setWeights({ ...weights, stalledDays: e.target.value })} /></label>
          </div>
          <div className="rev-radar__weights-actions">
            <button type="button" className="mai-btn mai-btn-ghost" onClick={() => setEditingWeights(false)} disabled={busy === 'weights'}>Cancelar</button>
            <button type="button" className="mai-btn" onClick={() => void saveWeights()} disabled={busy === 'weights'}>Salvar pesos</button>
          </div>
        </div>
      )}

      {list && (
        <div className="rev-radar__list" data-testid={`radar-list-${list}`}>
          <h4>
            {LIST_TITLES[list]} <span className="rev-radar__muted">({total})</span>
          </h4>
          {items.length === 0 ? (
            <p className="rev-radar__muted">Nada aqui agora.</p>
          ) : (
            <ul>
              {items.map((i) => (
                <li key={i.cardId} className="rev-row">
                  <button type="button" className="rev-row__name" onClick={() => onOpen(i.path)}>
                    {i.contactName}
                  </button>
                  <span className="rev-row__meta">
                    {i.title ? `${i.title} · ` : ''}{i.columnName}
                    {list === 'paradas' && ` · parada há ${daysLabel(i.daysStalled)}`}
                    {list === 'fechados' && i.wonAt && ` · ${new Date(i.wonAt).toLocaleDateString('pt-BR')}`}
                  </span>
                  <TemperatureChip temperature={i.temperature} evidence={i.temperatureEvidence} compact />
                  {list === 'sem-valor' ? (
                    <span className="rev-row__value-edit">
                      <input
                        aria-label={`Valor de ${i.contactName} (R$)`}
                        inputMode="decimal"
                        placeholder="R$"
                        value={values[i.cardId] ?? ''}
                        onChange={(e) => setValues((v) => ({ ...v, [i.cardId]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') void saveValue(i); }}
                      />
                      <button type="button" className="mai-btn mai-btn-soft" disabled={busy === i.cardId} onClick={() => void saveValue(i)}>
                        Salvar
                      </button>
                    </span>
                  ) : (
                    <span className="rev-row__value">{i.value != null ? formatMoney(i.value, i.currency) : 'Sem valor'}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {total > items.length && (
            <p className="rev-radar__muted">Mostrando {items.length} de {total}.</p>
          )}
        </div>
      )}
    </section>
  );
};

export default RevenueRadar;
